import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { CharacterRig } from './character-rig.js';
import { SKIN_VOLUME, VolumeSkinnedMesh } from './volume-skin.js';

/** Short tapered clumps grow from the skin and share its bone weights. Clothing covers the coat. */
export function addBugbearFur(rig: CharacterRig, color: number) {
  const skins: THREE.SkinnedMesh[] = [];
  rig.root.traverse(object => {
    if (object instanceof THREE.SkinnedMesh && !Array.isArray(object.material) && object.material.name === 'skin') skins.push(object);
  });
  const material = new THREE.MeshStandardMaterial({ color, vertexColors: true, roughness: 1, side: THREE.DoubleSide });
  material.name = 'bugbear-fur';
  let seed = 73;
  const p = rig.proportions, wrist = p.shoulderY + p.socketY - p.upperArm - p.forearm;
  const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  const surfaces: THREE.BufferGeometry[] = [];
  for (const skin of skins) {
    const source = skin.geometry, positions = source.getAttribute('position'), normals = source.getAttribute('normal');
    const index = source.index, count = index?.count ?? positions.count;
    const output: Record<string, number[]> = { position: [], color: [], skinIndex: [], skinWeight: [], [SKIN_VOLUME]: [] };
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), normal = new THREE.Vector3(), center = new THREE.Vector3();
    const edge = new THREE.Vector3(), tangent = new THREE.Vector3(), down = new THREE.Vector3(0, -1, 0);
    // A dark undercoat joins the short locks into a continuous coat instead of orange flecks on bare skin.
    const colors = source.getAttribute('color');
    if (colors) for (let i = 0; i < positions.count; i++) {
      center.fromBufferAttribute(positions, i); normal.fromBufferAttribute(normals, i);
      if (center.y < .12 || (center.y > p.neckY && normal.z > .35) || (Math.abs(center.x) > p.shoulderHalfWidth + .12 && center.y < wrist + .035)) continue;
      const shade = .72 + Math.sin(center.x * 61 + center.y * 79 + center.z * 43) * .04;
      colors.setXYZ(i, colors.getX(i) * shade, colors.getY(i) * shade, colors.getZ(i) * shade);
    }
    for (let i = 0; i < count; i += 3) {
      const ids = [0, 1, 2].map(offset => index ? index.getX(i + offset) : i + offset);
      a.fromBufferAttribute(positions, ids[0]); b.fromBufferAttribute(positions, ids[1]); c.fromBufferAttribute(positions, ids[2]);
      const area = edge.subVectors(b, a).cross(tangent.subVectors(c, a)).length() / 2;
      const expected = area * 1800, clumps = Math.floor(expected) + (random() < expected % 1 ? 1 : 0);
      for (let tuft = 0; tuft < clumps; tuft++) {
        const u = Math.sqrt(random()), v = random(), weights = [1 - u, u * (1 - v), u * v];
        center.set(0, 0, 0); normal.set(0, 0, 0);
        for (let j = 0; j < 3; j++) {
          center.addScaledVector(edge.fromBufferAttribute(positions, ids[j]), weights[j]);
          normal.addScaledVector(edge.fromBufferAttribute(normals, ids[j]), weights[j]);
        }
        normal.normalize();
        // Keep the muzzle, eyes and palms readable; sideburns and the back of the skull stay shaggy.
        if (center.y < .12 || (center.y > p.neckY && normal.z > .35) || (Math.abs(center.x) > p.shoulderHalfWidth + .12 && center.y < wrist + .035)) continue;
        tangent.crossVectors(normal, down);
        if (tangent.lengthSq() < .01) tangent.crossVectors(normal, new THREE.Vector3(1, 0, 0));
        tangent.normalize();
        const width = .005 + random() * .004, length = .018 + random() * .022;
        const root = center.clone().addScaledVector(normal, .001);
        const vertices = [root.clone().addScaledVector(tangent, -width), root.clone().addScaledVector(tangent, width), root.clone().addScaledVector(normal, length * .30).addScaledVector(down, length)];
        const nearest = ids[weights.indexOf(Math.max(...weights))], tone = .68 + random() * .25;
        for (let j = 0; j < 3; j++) {
          output.position.push(...vertices[j].toArray());
          const shade = j === 2 ? tone * 1.12 : tone; output.color.push(shade, shade, shade);
          for (const key of ['skinIndex', 'skinWeight']) {
            const attribute = source.getAttribute(key);
            output[key].push(attribute.getX(nearest), attribute.getY(nearest), attribute.getZ(nearest), attribute.getW(nearest));
          }
          output[SKIN_VOLUME].push(source.getAttribute(SKIN_VOLUME)?.getX(nearest) ?? 0);
        }
      }
    }
    if (!output.position.length) continue;
    const geometry = new THREE.BufferGeometry();
    for (const [key, values] of Object.entries(output)) geometry.setAttribute(key, key === 'skinIndex' ? new THREE.Uint16BufferAttribute(values, 4) : new THREE.Float32BufferAttribute(values, key === SKIN_VOLUME ? 1 : key === 'skinWeight' ? 4 : 3));
    geometry.computeVertexNormals();
    surfaces.push(geometry);
  }
  if (!surfaces.length) { material.dispose(); return; }
  const geometry = mergeGeometries(surfaces)!;
  for (const surface of surfaces) surface.dispose();
  const coat = new VolumeSkinnedMesh(geometry, material); coat.name = 'bugbear-fur';
  coat.castShadow = coat.receiveShadow = true; coat.boundingSphere = skins[0].boundingSphere?.clone() ?? null;
  rig.root.add(coat); coat.bind(rig.skeleton, skins[0].bindMatrix);
}
